import type { Citation } from '../model/citation';

import type { ClinicalTrial } from '@/services/getUserClinicalTrials';
import { formatTrialPhase, formatTrialStatus } from '@/util/formatClinicalTrial';

const TRIAL_REGISTRY = 'ClinicalTrials.gov';

/** Phase and status as one line, "Phase 3 · Recruiting", or null when neither is known. */
function summarizeTrial(trial: ClinicalTrial): string | null {
  return (
    [formatTrialPhase(trial.phase), formatTrialStatus(trial.status)].filter(Boolean).join(' · ') ||
    null
  );
}

/**
 * Maps a profile clinical trial onto a citation. The profile record names no
 * sponsor, so the citation has no author; an untitled trial is cited by its
 * NCT number, which is how CT.gov itself lists one.
 */
export function clinicalTrialToCitation(trial: ClinicalTrial): Citation {
  return {
    key: trial.nctId,
    title: trial.title ?? trial.nctId,
    authors: [],
    journal: TRIAL_REGISTRY,
    year: trial.year,
    pmid: null,
    url: trial.url,
    nct: trial.nctId,
    summary: summarizeTrial(trial),
  };
}

/**
 * Narrows the list as the expert types. Every word must appear somewhere in
 * the title, NCT number, conditions, phase, status or year, so `nsclc phase 3`
 * finds the phase 3 lung trial. Keeps the server's recency order.
 */
export function filterClinicalTrials(trials: ClinicalTrial[], query: string): ClinicalTrial[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return trials;
  }

  return trials.filter((trial) => {
    const haystack = [
      trial.title ?? '',
      trial.nctId,
      trial.conditions.join(' '),
      summarizeTrial(trial) ?? '',
      trial.year ? String(trial.year) : '',
    ]
      .join(' ')
      .toLowerCase();

    return words.every((word) => haystack.includes(word));
  });
}
